import React, { useCallback, useEffect, useState, useMemo } from 'react'
import FooterNav from './components/FooterNav'
import PageHeader from './components/PageHeader'
import { db, auth, functions } from "./config/firebase";
import { useNavigate } from 'react-router-dom'
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import "./TeeTimes.css";
import moment from 'moment';
import AvailableTimes from './components/AvailableTimes';
import TeeTimeForm from './components/TeeTimeForm';
import ConfirmReservationModal from './components/ConfirmReservationModal';
import { useAuthState } from 'react-firebase-hooks/auth';
import PageLoader from './components/PageLoader';
import LoadingScreen from './components/LoadingScreen';
import PayNowModal from './components/PayNowModal';
import ChooseCourseModal from './components/ChooseCourseModal';
import ConflictingReservationModal from './components/ConflictingReservationModal';
import { User } from 'firebase/auth';
import { SESSION_GROUP_ONLY_ENTRY } from './constants/entryFlow';
import { doc, getDoc, getDocs, collection, query, where, limit, onSnapshot, updateDoc, deleteDoc, addDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { buildSlotOccupancy, buildPendingHoldExpiry, canBookSlot } from './utils/teeSlotCapacity';
import {
    bookingProfileBlockState,
    getBookingProfileBlockReason,
    isBookingProfileComplete,
} from './utils/bookingContactRequirements';
import { resolvePrimaryGolferName } from './utils/golferName';
import { isSignedInUser } from './utils/authSession';
// import { getSunrise, getSunset } from 'sunrise-sunset-js';

interface Customer {
    email: string | null;
    phone: string | null;
    firstName: string | null;
    lastName: string | null;
    paid: boolean;
}

interface PayNowModalProps {
    customer: Customer;
    checkoutError: string | null;
    setCheckoutError: (error: string | null) => void;
    cartTotal: number;
    widgetSettings: any;
    updateReservationReceipt: (receipt: any, provider: string) => void;
    showPaymentModal: boolean;
    setShowPaymentModal: (show: boolean) => void;
    requirePayment: boolean;
    reservation: any;
    setPageLoaderStatus: (status: boolean, ariaLabel?: string) => void;
    finalizeReservation: () => boolean | Promise<boolean>;
    assertSlotAvailable: () => Promise<boolean>;
    removeReservationTime: () => void;
    authData: {
        apiLoginID: string;
        clientKey: string;
    };
    userData: any;
    user: User | null;
    course: any;
    setReservation: (reservation: any) => void;
}

/** Supports `users/{authUid}` and legacy `users/{autoId}` docs created with `addDoc` + `uid` field. */
async function loadUserProfileData(uid: string) {
    const directRef = doc(db, 'users', uid);
    const legacyQuery = query(collection(db, 'users'), where('uid', '==', uid), limit(1));

    const [directSnap, legacySnap] = await Promise.all([getDoc(directRef), getDocs(legacyQuery)]);

    if (directSnap.exists()) {
        return directSnap.data();
    }
    if (!legacySnap.empty) {
        return legacySnap.docs[0].data();
    }
    return null;
}

/** Shared cap for one-off Firestore profile reads (initial load + privilege check). */
const PROFILE_FIRESTORE_READ_MS = 20_000;
const RESERVE_FIRESTORE_MS = 35_000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
    return new Promise((resolve, reject) => {
        const t = window.setTimeout(() => reject(new Error(`${label}_TIMEOUT`)), ms);
        promise
            .then((v) => {
                clearTimeout(t);
                resolve(v);
            })
            .catch((e) => {
                clearTimeout(t);
                reject(e);
            });
    });
}

export default function TeeTimes() {
    const [pageLoaderStatus, setPageLoaderStatusRaw] = useState(false);
    const [pageLoaderAriaLabel, setPageLoaderAriaLabel] = useState('Loading, please wait');
    const setPageLoaderStatus = useCallback((open: boolean, ariaLabel?: string) => {
        setPageLoaderStatusRaw(open);
        if (open && ariaLabel) {
            setPageLoaderAriaLabel(ariaLabel);
        }
        if (!open) {
            setPageLoaderAriaLabel('Loading, please wait');
        }
    }, []);
    const [user, authLoading] = useAuthState(auth as any); // Type assertion to fix Firebase auth type mismatch
    const [userData, setUserData] = useState(null);
    /** Start true so we do not flash "no profile" before the first Firestore read runs. */
    const [userDataLoading, setUserDataLoading] = useState(true);
    const [userDataError, setUserDataError] = useState<string | null>(null);
    // const [triggerResetTimes, setTriggerResetTimes] = useState(null);
    const [requirePayment, setRequirePayment] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    // const [sunrise, setSunrise] = useState('');
    // const [sunset, setSunset] = useState('');
    const [checkoutError, setCheckoutError] = useState<string | null>(null);
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [showChooseCourseModal, setShowChooseCourseModal] = useState(true);
    const [pickFromGroupCatalog, setPickFromGroupCatalog] = useState(
        () =>
            typeof sessionStorage !== 'undefined' &&
            sessionStorage.getItem(SESSION_GROUP_ONLY_ENTRY) === '1'
    );
    const [todaysTeeTimeGroups, setTodaysTeeTimeGroups] = useState(null);
    const [showConflictingReservationModal, setShowConflictingReservationModal] = useState(false);
    const [reservationConfirmError, setReservationConfirmError] = useState<string | null>(null);
    const [bookingDenied, setBookingDenied] = useState(false);
    const [denyMessage, setDenyMessage] = useState('');
    const [checkingPrivileges, setCheckingPrivileges] = useState(false);
    const [privilegeCheckError, setPrivilegeCheckError] = useState<string | null>(null);
    const [profileLoadRetryKey, setProfileLoadRetryKey] = useState(0);
    const navigate = useNavigate();

    const retryProfileLoad = useCallback(() => {
        setUserDataError(null);
        setProfileLoadRetryKey((k) => k + 1);
    }, []);

    const goToProfileForBooking = useCallback(() => {
        const message = getBookingProfileBlockReason(user, userData);
        if (message) {
            navigate('/page/profile', { state: bookingProfileBlockState(message) });
        }
    }, [navigate, user, userData]);

    const bookingProfileIncomplete =
        isSignedInUser(user) &&
        !userDataLoading &&
        !userDataError &&
        !!userData &&
        !isBookingProfileComplete(user, userData);

    useEffect(() => {
        if (authLoading) return;
        if (!isSignedInUser(user)) {
            navigate('/', { replace: true });
        }
    }, [user, authLoading, navigate]);

    const bookingProfileBlockMessage = userData
        ? getBookingProfileBlockReason(user, userData)
        : null;

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            setUserDataError(null);
            if (!user?.uid) {
                setUserData(null);
                setUserDataLoading(false);
                return;
            }
            setUserDataLoading(true);
            setUserData(null);
            const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
            console.log('[TRWidget] initial profile load start', { uid: user.uid, profileLoadRetryKey });
            try {
                const data = await withTimeout(
                    loadUserProfileData(user.uid),
                    PROFILE_FIRESTORE_READ_MS,
                    'INITIAL_PROFILE'
                );
                if (cancelled) return;
                const elapsed = typeof performance !== 'undefined' ? Math.round(performance.now() - t0) : 0;
                console.log('[TRWidget] initial profile load complete', { ms: elapsed, hasData: !!data });
                if (data) {
                    setUserData(data);
                } else {
                    setUserData(null);
                }
            } catch (err: any) {
                if (cancelled) return;
                const elapsed = typeof performance !== 'undefined' ? Math.round(performance.now() - t0) : 0;
                console.error('[TRWidget] initial profile load failed', { ms: elapsed, err });
                setUserData(null);
                if (err?.message === 'INITIAL_PROFILE_TIMEOUT') {
                    setUserDataError(
                        'Loading your profile timed out. Check your connection, then tap Try again.'
                    );
                } else {
                    setUserDataError(err?.message || 'Could not load your profile.');
                }
            } finally {
                if (!cancelled) {
                    setUserDataLoading(false);
                }
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [user?.uid, profileLoadRetryKey]);

    useEffect(() => {
        setCustomer({
            email: user?.email,
            phone: userData?.phone,
            firstName: userData?.firstName,
            lastName: userData?.lastName,
            paid: false
        });
    }, [user, userData]);

    const groupID = localStorage.getItem('group');
    const courseID = localStorage.getItem('course');

    const date = moment().format('YYYY-MM-DD');
    const [reservation, setReservation] = useState({ id: null, expiration: null, expiresAt: null, date: date, numPlayers: null, numBuckets: null, numCarts: null, numHoles: null, time: null, startingTee: null, reroundTee: null, roundLength: null, golfers: [], receipt: "", source: 'reservationWidget' });

    const courseContextReady = !!(groupID && courseID);
    const groupCourseRef = useMemo(
        () =>
            courseContextReady
                ? doc(db, 'groups', groupID as string, 'courses', courseID as string)
                : null,
        [courseContextReady, groupID, courseID]
    );
    const teeTimesRef = useMemo(
        () =>
            courseContextReady
                ? collection(
                    db,
                    'groups',
                    groupID as string,
                    'courses',
                    courseID as string,
                    'teetimegroups'
                )
                : null,
        [courseContextReady, groupID, courseID]
    );

    const [widgetSettings, setWidgetSettings] = useState(null);
    const [courseRates, setCourseRates] = useState(null);
    const [authData, setAuthData] = useState(null);
    const [rates, setRates] = useState({
        nineHoleRate: 0.00,
        eighteenHoleRate: 0.00,
        nineHoleWithCartRate: 0.00,
        eighteenHoleWithCartRate: 0.00,
    });
    const [course, setCourse] = useState(null);

    const handleTeeGroupGolfersInputChange = ({ event, index }) => {
        const { value, name } = event.target;
        try {
            setReservation(prev => {
                const updatedGolfers = prev.golfers?.map((g, i) => {
                    if (index === i) {
                        // Ensure rateGroup is a number when updating
                        if (name === 'rateGroup') {
                            return { ...g, [name]: Number(value) || 0 };
                        }
                        return { ...g, [name]: value };
                    }
                    return g;
                });
                return { ...prev, golfers: updatedGolfers };
            });
        } catch (error) {
            console.log('Error updating golfer:', error);
        } finally {
            syncResWithDB();
        }
    }

    const syncResWithDB = async () => {
        if (!teeTimesRef || !reservation.id) return;
        const reservationRef = doc(teeTimesRef, reservation.id);
        await updateDoc(reservationRef, reservation);
    }

    useEffect(() => {
        if (!groupCourseRef) return;
        try {
            const getCourseData = async () => {
                const courseDoc = await getDoc(groupCourseRef);
                if (courseDoc.exists()) {
                    setCourse(courseDoc.data());
                }
            };
            getCourseData();
        } catch (err) {
            console.error("Error: ", err);
        }
    }, [courseID, groupID, groupCourseRef]);

    useEffect(() => {
        if (!groupCourseRef) return;
        try {
            const getWidgetSettings = async () => {
                const courseDoc = await getDoc(groupCourseRef);
                if (courseDoc.exists()) {
                    const data = courseDoc.data();
                    console.log('Course data:', data);
                    console.log('Widget settings:', data.widgetSettings);
                    setWidgetSettings(data.widgetSettings);
                    setCourseRates(data.rates);
                }
            };
            getWidgetSettings();
        } catch (err) {
            console.error("Error: ", err);
        }
    }, [courseID, groupCourseRef]);

    /** Single subscription with cleanup — avoids leaking Firestore listeners on each widgetSettings/date churn. */
    useEffect(() => {
        if (!teeTimesRef || !reservation?.date) {
            setTodaysTeeTimeGroups(null);
            return;
        }
        const q = query(teeTimesRef, where('date', '==', reservation.date));
        console.log('[TRWidget] teetimegroups onSnapshot subscribe', {
            date: reservation.date,
            groupID,
            courseID
        });
        let unsubscribe: (() => void) | undefined;
        try {
            unsubscribe = onSnapshot(
                q,
                (snapshot) => {
                    const mappedData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    setTodaysTeeTimeGroups(mappedData);
                },
                (err) => {
                    console.error('[TRWidget] teetimegroups snapshot listener error', err);
                }
            );
        } catch (err) {
            console.error('[TRWidget] teetimegroups snapshot setup failed', err);
            return;
        }
        return () => {
            console.log('[TRWidget] teetimegroups onSnapshot unsubscribe', { date: reservation.date });
            unsubscribe?.();
        };
    }, [teeTimesRef, reservation?.date, groupID, courseID]);

    useEffect(() => {
        setRequirePayment(widgetSettings?.requirePayment);
        console.log("Payment required:", widgetSettings?.requirePayment);
        setRates({
            nineHoleRate: parseInt(widgetSettings?.nineHoleRate),
            eighteenHoleRate: parseInt(widgetSettings?.eighteenHoleRate),
            nineHoleWithCartRate: parseInt(widgetSettings?.nineHoleWithCartRate),
            eighteenHoleWithCartRate: parseInt(widgetSettings?.eighteenHoleWithCartRate),
        });
        setAuthData({
            apiLoginID: widgetSettings?.paymentGateway?.apiLoginId,
            clientKey: widgetSettings?.paymentGateway?.clientKey,
        });
        //eslint-disable-next-line
    }, [widgetSettings]);


    const removeReservationTime = async () => {
        if (reservation.id !== null && teeTimesRef) {
            try {
                const reservationRef = doc(teeTimesRef, reservation.id);
                await deleteDoc(reservationRef);
                setReservation(prevState => ({ ...prevState, time: null, startingTee: null, reroundTee: null, expiration: null, expiresAt: null, id: null }));
            } catch (err) {
                console.log('error deleting ==>>', err);
            }
        }
    }

    const updateReservationReceipt = async (receipt, provider) => {
        console.log('updateReservationReceipt called with:', { receipt, provider });

        let receiptNumber;
        if (provider === 'square') {
            // Handle both possible structures for backward compatibility
            receiptNumber = receipt?.payment?.id || receipt?.id;
            console.log('Extracted receipt number:', receiptNumber);
        } else {
            receiptNumber = receipt?.transId;
        }

        if (reservation.id !== null && teeTimesRef) {
            try {
                const reservationRef = doc(teeTimesRef, reservation.id);
                const updateData: {
                    paymentProcessor: string;
                    receipt?: any;
                    receiptNumber?: string;
                    golfers?: any[];
                } = {
                    paymentProcessor: provider
                };

                // Only add receipt and receiptNumber if they exist
                if (receipt) {
                    updateData.receipt = receipt;
                }
                if (receiptNumber) {
                    updateData.receiptNumber = receiptNumber;
                }
                // Add updated golfers if they exist in the receipt
                if (receipt?.golfers) {
                    updateData.golfers = receipt.golfers;
                }

                console.log('Updating reservation with:', updateData);

                await updateDoc(reservationRef, updateData);
                console.log('Receipt updated successfully:', { receiptNumber, provider });
            } catch (error) {
                console.error("Error adding receipt: ", error);
            }
        } else {
            console.error('Cannot update receipt: reservation.id is null');
        }
    }

    const updateReservation = e => {
        e.persist();
        let value = e.target.value;
        if (value === "null") { value = null }
        if (e.target.type === 'radio') {
            if (e.target.getAttribute('data-type') === "integer") {
                value = parseInt(e.target.getAttribute('data-radio-value'));
            } else {
                value = e.target.getAttribute('data-radio-value');
            }
        }
        if (e.target.getAttribute('data-reset-time') === "true") {
            removeReservationTime();
        }
        if (e.target.getAttribute('data-tee-time')) {
            if (bookingProfileIncomplete) {
                goToProfileForBooking();
                return;
            }
            setReservationConfirmError(null);
            /* Reserve a pending time if one is selected */
            let time = e.target.getAttribute('data-tee-time');
            let teeSheet = e.target.getAttribute('data-tee-sheet');
            let reroundTee = e.target.getAttribute('data-reround-tee');
            let roundLength = e.target.getAttribute('data-round-length');
            let requirePayment = e.target.getAttribute('data-require-payment') === "true";
            const { expiresAtDate, expiration } = buildPendingHoldExpiry();
            setReservation(prevState => ({
                ...prevState,
                time: time,
                startingTee: teeSheet,
                reroundTee: reroundTee,
                expiration: expiration,
                expiresAt: Timestamp.fromDate(expiresAtDate),
                roundLength: roundLength,
                requirePayment: requirePayment
            }));
        } else {
            // If we're updating the number of players, we need to update the golfers array
            if (e.target.name === 'numPlayers') {
                setReservation(prevState => {
                    const currentGolfers = prevState.golfers || [];
                    const newNumPlayers = parseInt(value);

                    // Create new golfers array with default rate group
                    let newGolfers = [];

                    // Keep existing golfers up to the new number
                    for (let i = 0; i < newNumPlayers; i++) {
                        if (i < currentGolfers.length) {
                            // Keep existing golfer data
                            newGolfers.push(currentGolfers[i]);
                        } else {
                            // Add new golfer with default rate group
                            newGolfers.push({
                                name: i === 0 ? resolvePrimaryGolferName(userData) : "Guest",
                                email: i === 0 ? (user?.email || userData?.email || "") : "",
                                phone: i === 0 ? userData?.phone : "",
                                paid: false,
                                rateGroup: 0
                            });
                        }
                    }

                    return {
                        ...prevState,
                        [e.target.name]: value,
                        golfers: newGolfers
                    };
                });
            } else {
                setReservation(prevState => ({ ...prevState, [e.target.name]: value }));
            }
        }
    }

    useEffect(() => {
        console.log('Reservation: ', reservation);

    }, [reservation]);

    useEffect(() => {
        console.log('Todays Tee Time Groups: ', todaysTeeTimeGroups);

    }, [todaysTeeTimeGroups]);

    const assertSlotAvailable = useCallback(async (): Promise<boolean> => {
        if (!teeTimesRef || !reservation?.date || !reservation?.time || !reservation?.startingTee) {
            console.warn('[TRWidget] assertSlotAvailable missing required fields', {
                hasTeeTimesRef: !!teeTimesRef,
                date: reservation?.date,
                time: reservation?.time,
                startingTee: reservation?.startingTee,
            });
            setReservationConfirmError('Could not verify this tee time. Please select a time again.');
            return false;
        }

        setPageLoaderStatus(true, 'Checking availability…');
        try {
            const normalizedDate = moment(reservation.date).format('YYYY-MM-DD');
            const q = query(teeTimesRef, where('date', '==', normalizedDate));
            const snapshot = await getDocs(q);
            const groups = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

            const occupancy = buildSlotOccupancy(groups, {
                excludeReservationId: reservation.id,
                date: reservation.date,
            });

            const result = canBookSlot({
                occupancy,
                reservation: {
                    startingTee: reservation.startingTee,
                    time: reservation.time,
                    reroundTee: reservation.reroundTee,
                    roundLength: reservation.roundLength,
                    numHoles: reservation.numHoles,
                    numPlayers: reservation.numPlayers,
                    date: reservation.date,
                },
                widgetRules: widgetSettings?.rules,
                groups,
            });

            if (!result.ok) {
                console.log('Slot unavailable:', result.reason);
                setReservationConfirmError(null);
                setShowConflictingReservationModal(true);
                return false;
            }
            return true;
        } catch (err) {
            console.error('assertSlotAvailable failed:', err);
            setReservationConfirmError('Could not check tee time availability. Please try again.');
            return false;
        } finally {
            setPageLoaderStatus(false);
        }
    }, [
        teeTimesRef,
        reservation,
        widgetSettings?.rules,
        setPageLoaderStatus,
    ]);

    const handleReserveNow = async (): Promise<boolean> => {
        setReservationConfirmError(null);
        if (bookingProfileIncomplete) {
            goToProfileForBooking();
            return false;
        }
        const available = await assertSlotAvailable();
        if (!available) return false;
        return finalizeReservation();
    };

    const finalizeReservation = async (): Promise<boolean> => {
        setPageLoaderStatus(true, 'Confirming reservation…');
        console.log('FINALIZED RESERVATION:', reservation);

        if (bookingProfileIncomplete) {
            setPageLoaderStatus(false);
            goToProfileForBooking();
            return false;
        }

        if (reservation.id === null || !teeTimesRef || !groupID || !courseID) {
            setPageLoaderStatus(false);
            setReservationConfirmError('Reservation could not be confirmed. Please select a tee time again.');
            return false;
        }

        try {
            const reservationRef = doc(teeTimesRef, reservation.id);
            const updatedGolfers = (reservation.golfers || []).map((golfer) => ({
                ...golfer,
                rateGroup: golfer.rateGroup,
            }));

            await updateDoc(reservationRef, { golfers: updatedGolfers });

            const confirmFn = httpsCallable<
                { groupId: string; courseId: string; reservationId: string },
                { success: boolean }
            >(functions, 'confirmTeeTimeReservation');

            const { data: confirmData } = await confirmFn({
                groupId: groupID,
                courseId: courseID,
                reservationId: reservation.id,
            });

            setPageLoaderStatus(false);
            if (confirmData?.success !== false) {
                navigate('/page/reservations');
            }
            return true;
        } catch (error: any) {
            console.error('Error finalizing reservation:', error);
            setPageLoaderStatus(false);
            const slotUnavailable =
                error?.code === 'functions/failed-precondition' ||
                error?.details?.code === 'SLOT_UNAVAILABLE';
            if (slotUnavailable) {
                setReservationConfirmError(null);
                setShowConflictingReservationModal(true);
            } else {
                const raw = error?.message || '';
                const message =
                    error?.code === 'functions/internal' || raw === 'INTERNAL' || raw === 'internal'
                        ? 'Could not confirm your reservation due to a server error. Please try again in a moment.'
                        : raw ||
                          'Could not confirm your reservation. Check your connection and try again.';
                setReservationConfirmError(message);
            }
            return false;
        }
    };

    const reservePendingTeeTime = async () => {
        try {
            if (reservation.time && teeTimesRef) {
                if (bookingProfileIncomplete) {
                    return;
                }

                const primaryName = resolvePrimaryGolferName(userData);

                // Ensure rateGroup is a number for the first golfer
                let golfers = [{
                    email: user?.email || userData?.email || null,
                    phone: userData?.phone,
                    name: primaryName,
                    paid: false,  // Explicitly set to false
                    rateGroup: 0  // Explicitly set to 0
                }];

                // Add additional golfers with explicit rateGroup
                for (var i = 1; i < reservation.numPlayers; i++) {
                    golfers.push({
                        name: "Guest",
                        email: "",
                        phone: "",
                        paid: false,  // Explicitly set to false
                        rateGroup: 0  // Explicitly set to 0
                    });
                }

                console.log('[TRWidget] reservePendingTeeTime initializing golfers:', golfers);

                const { expiresAtDate, expiration } = buildPendingHoldExpiry();
                const expiresAt = Timestamp.fromDate(expiresAtDate);

                let newTeeGroup = {
                    date: reservation?.date,
                    startingTime: moment(reservation?.time, 'h:mm a').format('h:mm a'),
                    startingTeeID: reservation?.startingTee,
                    reroundTeeID: reservation?.reroundTee,
                    numHoles: parseInt(reservation?.numHoles),
                    expiresAt,
                    expiration,
                    reroundTime: moment(reservation?.time, 'h:mm a').add(reservation?.roundLength, 'minutes').format('h:mm a'),
                    status: 'pending',
                    receipt: reservation?.receipt,
                    uid: user?.uid,
                    golfers: golfers,
                    source: 'widget'
                }

                try {
                    if (reservation.id !== null) {
                        const reservationRef = doc(teeTimesRef, reservation.id);
                        await withTimeout(
                            updateDoc(reservationRef, newTeeGroup),
                            RESERVE_FIRESTORE_MS,
                            'RESERVE_FIRESTORE'
                        );
                        setReservation(prevState => ({
                            ...prevState,
                            expiresAt,
                            expiration,
                            golfers: golfers,
                        }));
                    } else {
                        const teeGroupRef = await withTimeout(
                            addDoc(teeTimesRef, newTeeGroup),
                            RESERVE_FIRESTORE_MS,
                            'RESERVE_FIRESTORE'
                        );
                        setReservation(prevState => ({
                            ...prevState,
                            id: teeGroupRef.id,
                            expiresAt,
                            expiration,
                            golfers: golfers,
                        }));
                    }
                } catch (err: any) {
                    const timedOut = err?.message === 'RESERVE_FIRESTORE_TIMEOUT';
                    console.error(
                        timedOut
                            ? '[TRWidget] reservePendingTeeTime Firestore timed out'
                            : '[TRWidget] reservePendingTeeTime Firestore error',
                        err
                    );
                }
            }
        } finally {
            setPageLoaderStatus(false);
        }
    }
    useEffect(() => {
        setPageLoaderStatus(true, 'Saving tee time…');
        void reservePendingTeeTime();
        determinePaymentRequirement();
        //eslint-disable-next-line
    }, [reservation.time]);

    useEffect(() => {

        setReservation(prevState => ({ ...prevState, numPlayers: null, numHoles: null }));

        //eslint-disable-next-line
    }, [reservation.date]);

    const determinePaymentRequirement = () => {
        // Set Default based on widget settings
        setRequirePayment(widgetSettings?.requirePayment);
        console.log("Payment required:", widgetSettings?.requirePayment);

        // See if any rules override the default above
        widgetSettings?.rules?.map((rule) => {
            if (rule.active === true) {
                // console.log('Active Rule +++++', rule.name);
                if (rule.when.days.includes(moment(reservation.date).format('dddd'))
                    && moment(reservation.time, 'h:mm a') >= moment(rule.startTime, 'h:mm a')
                    && moment(reservation.time, 'h:mm a') <= moment(rule.endTime, 'h:mm a')
                ) {
                    // console.log('Rule IS in Effect +++++', rule.name + ": " + rule.startTime + " and " + rule.endTime + "compared with " + reservation.time);
                    if (rule.requirePayment === "always") {
                        setRequirePayment(true);
                    } else if (rule.requirePayment === "never") {
                        setRequirePayment(false);
                    } else {
                        setRequirePayment(widgetSettings.requirePayment)
                    }
                } else {
                    // console.log('Rule NOT in Effect +++++', rule.name + ": " + rule.startTime + " and " + rule.endTime + "compared with " + reservation.time);
                }
            }
            return console.log();
        });

        // Finally see if any users are flagged to always or never require payment
        if (Array.isArray(userData?.privileges)) {
            let privilegesIndex = userData?.privileges.findIndex((obj => obj.course === courseID && obj.group === groupID));
            if (privilegesIndex >= 0) {
                if (userData?.privileges[privilegesIndex]['requirePayment'] === 'always') {
                    setRequirePayment(true);
                }
                if (userData?.privileges[privilegesIndex]['requirePayment'] === 'never') {
                    setRequirePayment(false);
                }
            }
        }
    }

    const checkUserPrivileges = useCallback(async () => {
        if (!user?.uid || !groupID || !courseID) {
            console.log('[TRWidget] checkUserPrivileges skipped — missing IDs', {
                userExists: !!user,
                uid: user?.uid,
                groupID,
                courseID
            });
            setCheckingPrivileges(false);
            setPrivilegeCheckError(null);
            return;
        }

        setPrivilegeCheckError(null);
        setCheckingPrivileges(true);
        try {
            let profile: any = null;
            const useCachedProfile = !!userData && !userDataLoading;
            if (useCachedProfile) {
                profile = userData;
                console.log('[TRWidget] checkUserPrivileges using in-memory profile (skips duplicate Firestore read)');
            } else {
                console.log('[TRWidget] checkUserPrivileges fetching profile from Firestore');
                profile = await withTimeout(
                    loadUserProfileData(user.uid),
                    PROFILE_FIRESTORE_READ_MS,
                    'PRIVILEGE_FETCH'
                );
            }

            if (!profile) {
                console.log('[TRWidget] checkUserPrivileges — no profile document');
                setBookingDenied(false);
                setDenyMessage('');
                return;
            }

            const privileges = profile.privileges || [];
            const coursePrivilege = privileges.find(
                (p: { group?: string; course?: string }) => p.group === groupID && p.course === courseID
            );
            console.log('[TRWidget] checkUserPrivileges course privilege', { found: !!coursePrivilege });

            if (coursePrivilege?.denyOnlineBooking === true) {
                setBookingDenied(true);
                setDenyMessage(
                    coursePrivilege.denyOnlineBookingMessage ||
                        'Online booking is not available for this course.'
                );
            } else {
                setBookingDenied(false);
                setDenyMessage('');
            }
        } catch (error: any) {
            const timedOut = error?.message === 'PRIVILEGE_FETCH_TIMEOUT';
            console.error(
                timedOut ? '[TRWidget] checkUserPrivileges timed out' : '[TRWidget] checkUserPrivileges error',
                error
            );
            if (timedOut) {
                setPrivilegeCheckError(
                    'Checking your booking access is taking longer than expected. Check your connection and tap Try again.'
                );
            } else {
                setPrivilegeCheckError(
                    error?.message || 'Could not verify booking access. Tap Try again.'
                );
            }
            setBookingDenied(false);
            setDenyMessage('');
        } finally {
            setCheckingPrivileges(false);
        }
    }, [user, groupID, courseID, userData, userDataLoading]);

    // Check privileges whenever the form would be shown
    useEffect(() => {
        if (showChooseCourseModal) {
            setPrivilegeCheckError(null);
            return;
        }
        void checkUserPrivileges();
    }, [showChooseCourseModal, user, groupID, courseID, checkUserPrivileges]);

    if (authLoading || !isSignedInUser(user)) {
        return <LoadingScreen />;
    }

    return (
        <div className="appScreenContainer">
            <ChooseCourseModal
                user={user}
                authLoading={authLoading}
                userData={userData}
                userDataLoading={userDataLoading}
                userDataError={userDataError}
                showChooseCourseModal={showChooseCourseModal}
                setShowChooseCourseModal={setShowChooseCourseModal}
                pickFromGroupCatalog={pickFromGroupCatalog}
                catalogGroupId={groupID}
                onGroupCatalogComplete={() => setPickFromGroupCatalog(false)}
                refreshUserProfile={async () => {
                    if (!user?.uid) return;
                    setUserDataError(null);
                    setUserDataLoading(true);
                    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
                    try {
                        const data = await withTimeout(
                            loadUserProfileData(user.uid),
                            PROFILE_FIRESTORE_READ_MS,
                            'REFRESH_PROFILE'
                        );
                        const elapsed = typeof performance !== 'undefined' ? Math.round(performance.now() - t0) : 0;
                        console.log('[TRWidget] refreshUserProfile complete', { ms: elapsed, hasData: !!data });
                        setUserData(data ?? null);
                    } catch (err: any) {
                        const elapsed = typeof performance !== 'undefined' ? Math.round(performance.now() - t0) : 0;
                        console.error('[TRWidget] refreshUserProfile failed', { ms: elapsed, err });
                        setUserData(null);
                        if (err?.message === 'REFRESH_PROFILE_TIMEOUT') {
                            setUserDataError(
                                'Refreshing your profile timed out. Check your connection, then tap Try again.'
                            );
                        } else {
                            setUserDataError(err?.message || 'Could not load your profile.');
                        }
                    } finally {
                        setUserDataLoading(false);
                    }
                }}
                onRetryProfileLoad={retryProfileLoad}
            />
            <PageHeader pageTitle={"Reserve a Tee Time"} />

            {checkingPrivileges ? (
                <div
                    className="d-flex flex-column justify-content-center align-items-center gap-2"
                    style={{ minHeight: '200px' }}
                    data-trwidget-loader="privileges"
                    aria-busy="true"
                    aria-live="polite"
                >
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Checking booking access</span>
                    </div>
                    <p className="text-muted small mb-0 text-center px-3">Checking booking access…</p>
                </div>
            ) : privilegeCheckError ? (
                <div className="alert alert-warning mx-3" role="alert">
                    <p className="mb-2">{privilegeCheckError}</p>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                            setPrivilegeCheckError(null);
                            void checkUserPrivileges();
                        }}
                    >
                        Try again
                    </button>
                </div>
            ) : bookingDenied ? (
                <div className="alert alert-danger mx-3 text-center">
                    {denyMessage || "You do not have access to online booking. Please contact management."}
                </div>
            ) : bookingProfileIncomplete ? (
                <div className="alert alert-warning mx-3" role="alert">
                    <p className="mb-3">{bookingProfileBlockMessage}</p>
                    <button type="button" className="btn btn-primary btn-sm" onClick={goToProfileForBooking}>
                        Go to Account
                    </button>
                </div>
            ) : !showChooseCourseModal && (
                <>
                    <TeeTimeForm
                        key={`${user?.uid}-${groupID}-${courseID}`}
                        reservation={reservation}
                        updateReservation={updateReservation}
                        widgetSettings={widgetSettings}
                        widgetRules={widgetSettings?.rules}></TeeTimeForm>

                    {reservationConfirmError && (
                        <div className="alert alert-danger mx-3" role="alert">
                            {reservationConfirmError}
                        </div>
                    )}

                    <AvailableTimes
                        reservation={reservation}
                        setPageLoaderStatus={setPageLoaderStatus}
                        updateReservation={updateReservation}
                        date={date}
                        widgetSettings={widgetSettings}
                        widgetRules={widgetSettings?.rules}
                        isSuperAdmin={userData?.globalRole === 'superadmin'}
                    ></AvailableTimes>
                </>
            )}

            <ConflictingReservationModal
                showConflictingReservationModal={showConflictingReservationModal}
                setShowConflictingReservationModal={setShowConflictingReservationModal}
                setPageLoaderStatus={setPageLoaderStatus}
                removeReservationTime={removeReservationTime}
            ></ConflictingReservationModal>

            <ConfirmReservationModal
                course={course}
                setCourse={setCourse}
                setShowPaymentModal={setShowPaymentModal}
                requirePayment={requirePayment}
                reservation={reservation}
                setPageLoaderStatus={setPageLoaderStatus}
                handleReserveNow={handleReserveNow}
                assertSlotAvailable={assertSlotAvailable}
                removeReservationTime={removeReservationTime}
                userData={userData}
                setReservation={setReservation}
                handleTeeGroupGolfersInputChange={handleTeeGroupGolfersInputChange}
                user={user}></ConfirmReservationModal>

            <PayNowModal
                customer={customer!}
                checkoutError={checkoutError}
                setCheckoutError={setCheckoutError}
                widgetSettings={widgetSettings}
                updateReservationReceipt={updateReservationReceipt}
                showPaymentModal={showPaymentModal}
                setShowPaymentModal={setShowPaymentModal}
                requirePayment={requirePayment}
                reservation={reservation}
                setReservation={setReservation}
                setPageLoaderStatus={setPageLoaderStatus}
                finalizeReservation={finalizeReservation}
                onPaymentSucceeded={() => navigate('/page/reservations')}
                assertSlotAvailable={assertSlotAvailable}
                removeReservationTime={removeReservationTime}
                authData={authData}
                userData={userData}
                user={user}
                course={course}
            ></PayNowModal>

            {pageLoaderStatus && <PageLoader ariaLabel={pageLoaderAriaLabel}></PageLoader>}

            <FooterNav></FooterNav>
        </div>
    )
}
